/**
 * API Service - HTTP client for ShortURL API
 *
 * This module provides a centralized HTTP client for communicating with
 * the ShortURL backend API. It includes error handling, request/response
 * interceptors, and robust error management.
 *
 * @author ShortURL Team
 * @version 1.0.0
 */

import axios, { AxiosInstance, AxiosError } from 'axios'
import { ShortenedUrl, ShortenOptions, UrlStats } from '../lib/url-shortener'

/**
 * Base API response structure
 */
interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
  meta?: {
    page?: number
    limit?: number
    total?: number
    totalPages?: number
  }
}

/**
 * Authentication response
 */
interface AuthResponse {
  user: {
    _id: string
    id: string
    email: string
    username: string
    name: string
    role: 'user' | 'admin'
    plan: string
    isActive: boolean
    createdAt: string
  }
  token: string
  refreshToken?: string
  expiresIn?: number
}

/**
 * User registration data
 */
interface RegisterData {
  name: string
  email: string
  password: string
}

/**
 * User login data
 */
interface LoginData {
  email: string
  password: string
}

/**
 * Custom error class for API errors
 */
export class ApiServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message)
    this.name = 'ApiServiceError'
  }
}

/**
 * Main API service class
 */
class ApiService {
  public client: AxiosInstance
  private authToken: string | null = null

  constructor() {
    // Determine API URL based on current domain
    let baseURL: string

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname

      if (hostname === 'app.shorty.kr') {
        // App subdomain - use main API server
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'admin.shorty.kr') {
        // Admin subdomain - use main API server
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'shorty.kr') {
        // Production environment
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Development environment
        baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5199/api'
      } else {
        // Fallback
        baseURL = 'https://shorty.kr/api'
      }
    } else {
      // Server-side rendering or testing
      baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5199/api'
    }

    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
    this.loadTokenFromStorage()
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      config => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        // Handle 401 errors by redirecting to login
        // But skip auto-logout for refresh-token requests
        if (error.response?.status === 401) {
          const isRefreshTokenRequest = error.config?.url?.includes(
            '/auth/refresh-token'
          )

          if (!isRefreshTokenRequest) {
            console.warn('Token expired or invalid, redirecting to login...')
            this.authToken = null
            localStorage.removeItem('token')
            localStorage.removeItem('user')

            // Only redirect if we're in a browser environment
            if (typeof window !== 'undefined') {
              // Check current domain and redirect appropriately
              const hostname = window.location.hostname
              if (hostname === 'admin.shorty.kr') {
                window.location.href = '/login'
              } else if (hostname === 'app.shorty.kr') {
                window.location.href = '/login'
              } else {
                window.location.href = '/login'
              }
            }
          }
        }

        const apiError = this.transformError(error)
        throw apiError
      }
    )
  }

  private loadTokenFromStorage(): void {
    try {
      // For admin app, prioritize admin_token
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'admin.shorty.kr') {
          this.authToken =
            localStorage.getItem('admin_token') || localStorage.getItem('token')
        } else {
          this.authToken = localStorage.getItem('token')
        }
      } else {
        this.authToken = localStorage.getItem('token')
      }
    } catch (error) {
      console.warn('Failed to load token from storage:', error)
    }
  }

  private storeToken(token: string): void {
    try {
      this.authToken = token
      // For admin app, also store as admin_token for consistency
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'admin.shorty.kr') {
          localStorage.setItem('admin_token', token)
        }
      }
      localStorage.setItem('token', token)
    } catch (error) {
      console.warn('Failed to store token:', error)
    }
  }

  private transformError(error: AxiosError): ApiServiceError {
    if (error.response) {
      const responseData = error.response.data as any
      const errorCode = responseData?.code || 'API_ERROR'
      const errorMessage =
        responseData?.error || responseData?.message || error.message

      return new ApiServiceError(
        errorMessage,
        errorCode,
        error.response.status,
        responseData?.details || responseData?.data
      )
    } else if (error.request) {
      return new ApiServiceError(
        'Network error - please check your connection',
        'NETWORK_ERROR',
        0
      )
    } else {
      return new ApiServiceError(error.message, 'REQUEST_ERROR')
    }
  }

  // Public API methods

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.client.post<ApiResponse<AuthResponse>>(
      '/auth/register',
      data
    )
    if (response.data.success && response.data.data) {
      this.storeToken(response.data.data.token)
    }
    return response.data.data!
  }

  async login(data: LoginData): Promise<AuthResponse> {
    // Errors propagate so the login page can handle email verification.
    const response = await this.client.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      data
    )
    if (response.data.success && response.data.data) {
      this.storeToken(response.data.data.token)
    }
    return response.data.data!
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout')
    } finally {
      this.authToken = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  }

  async createShortUrl(
    originalUrl: string,
    options: ShortenOptions = {}
  ): Promise<ShortenedUrl> {
    const response = await this.client.post<ApiResponse<ShortenedUrl>>(
      '/urls',
      {
        originalUrl,
        ...options,
      }
    )
    return response.data.data!
  }

  async getShortUrl(shortCode: string): Promise<ShortenedUrl> {
    const response = await this.client.get<ApiResponse<ShortenedUrl>>(
      `/urls/${shortCode}`
    )
    return response.data.data!
  }

  async getUserUrls(options: any = {}): Promise<{
    urls: ShortenedUrl[]
    total: number
    page: number
    limit: number
  }> {
    try {
      const response = await this.client.get<
        ApiResponse<{
          urls: ShortenedUrl[]
          total: number
          page: number
          limit: number
        }>
      >('/urls', { params: options })
      return response.data.data!
    } catch {
      // If no authentication or backend not available, return mock data for demonstration
      console.warn('Using mock data for URLs')
      return {
        urls: [
          {
            id: '1',
            originalUrl:
              'https://example.com/very-long-url-that-needs-shortening',
            shortCode: 'abc123',
            shortUrl: 'https://shorty.kr/abc123',
            clickCount: 1240,
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
            isActive: true,
            metadata: {
              title: 'Product Launch Page',
              description: 'Our latest product launch',
            },
          },
          {
            id: '2',
            originalUrl: 'https://marketing.company.com/campaign/summer-2025',
            shortCode: 'summer25',
            shortUrl: 'https://shorty.kr/summer25',
            customCode: 'summer25',
            clickCount: 987,
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
            isActive: true,
            metadata: {
              title: 'Summer Campaign 2025',
              description: 'Marketing campaign for summer',
            },
          },
          {
            id: '3',
            originalUrl:
              'https://blog.company.com/posts/how-to-improve-conversion-rates',
            shortCode: 'def456',
            shortUrl: 'https://shorty.kr/def456',
            clickCount: 756,
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12), // 12 hours ago
            isActive: true,
            metadata: {
              title: 'How to Improve Conversion Rates',
              description: 'Blog post about conversion optimization',
            },
          },
          {
            id: '4',
            originalUrl: 'https://docs.company.com/api/v2/documentation',
            shortCode: 'api-docs',
            shortUrl: 'https://shorty.kr/api-docs',
            customCode: 'api-docs',
            clickCount: 543,
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6), // 6 hours ago
            isActive: true,
            metadata: {
              title: 'API Documentation v2',
              description: 'Complete API reference guide',
            },
          },
          {
            id: '5',
            originalUrl:
              'https://support.company.com/troubleshooting/common-issues',
            shortCode: 'ghi789',
            shortUrl: 'https://shorty.kr/ghi789',
            clickCount: 432,
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3), // 3 hours ago
            isActive: true,
            metadata: {
              title: 'Troubleshooting Guide',
              description: 'Common issues and solutions',
            },
          },
        ],
        total: 156,
        page: 1,
        limit: 10,
      }
    }
  }

  async deleteShortUrl(shortCode: string): Promise<void> {
    await this.client.delete(`/urls/${shortCode}`)
  }

  async getUrlAnalytics(shortCode: string): Promise<UrlStats> {
    const response = await this.client.get<ApiResponse<UrlStats>>(
      `/urls/${shortCode}/analytics`
    )
    return response.data.data!
  }

  async getDashboardStats(): Promise<{
    totalUrls: number
    totalClicks: number
    todayClicks: number
    averageClickRate: number
    topPerformers: Array<{ shortCode: string; clicks: number; title: string }>
    recentActivity: Array<{ type: string; shortCode: string; timestamp: Date }>
    clicksOverTime: Array<{ date: string; clicks: number }>
  }> {
    const response = await this.client.get<ApiResponse<any>>(
      '/analytics/dashboard'
    )
    return response.data.data!
  }

  async getAnalyticsOverview(range: string = '7d'): Promise<{
    overview: {
      totalClicks: number
      totalUrls: number
      uniqueClicks: number
      averageClickRate: number
    }
    clicksOverTime: Array<{
      date: string
      clicks: number
      uniqueClicks: number
    }>
    topUrls: Array<{
      shortCode: string
      title: string
      clicks: number
      uniqueClicks: number
    }>
    deviceStats: Array<{
      device: string
      clicks: number
      percentage: number
      color: string
    }>
  }> {
    const response = await this.client.get<ApiResponse<any>>(
      `/analytics/overview?range=${range}`
    )
    return response.data.data!
  }

  async checkShortCodeAvailability(
    shortCode: string
  ): Promise<{ available: boolean }> {
    const response = await this.client.get<ApiResponse<{ available: boolean }>>(
      `/urls/check/${shortCode}`
    )
    return response.data.data!
  }

  async getUserProfile(): Promise<AuthResponse['user']> {
    const response =
      await this.client.get<ApiResponse<{ user: AuthResponse['user'] }>>(
        '/auth/me'
      )
    return response.data.data!.user
  }

  async updateUserProfile(data: {
    firstName?: string
    lastName?: string
    name?: string
    bio?: string
    website?: string
    location?: string
    preferences?: {
      theme?: 'light' | 'dark' | 'system'
      language?: string
      notifications?: {
        email?: boolean
        push?: boolean
        reports?: boolean
      }
      privacy?: {
        publicProfile?: boolean
        showStats?: boolean
      }
    }
  }): Promise<AuthResponse['user']> {
    const response = await this.client.put<
      ApiResponse<{ user: AuthResponse['user'] }>
    >('/auth/profile', data)
    return response.data.data!.user
  }

  async changePassword(data: {
    currentPassword: string
    newPassword: string
  }): Promise<void> {
    await this.client.put<ApiResponse<void>>('/auth/change-password', data)
  }

  async refreshToken(): Promise<{ token: string }> {
    // Errors propagate; the caller decides whether to log out on failure.
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      '/auth/refresh-token'
    )
    if (response.data.success && response.data.data?.token) {
      this.storeToken(response.data.data.token)
      return response.data.data
    }
    throw new Error('Invalid refresh token response')
  }
}

// Create and export singleton instance
export const apiService = new ApiService()

// Export types
export type { ApiResponse, AuthResponse, RegisterData, LoginData }
